import type { AuthKind } from "../lib/api";

interface AuthSpec {
  kind: AuthKind;
  fields: Record<string, string>;
}

interface Props {
  kind: AuthKind;
  fields: Record<string, string>;
  onChange: (next: AuthSpec) => void;
}

// Per-request auth editor. Lives in the new "Auth" tab inside
// RequestBuilder. The shape mirrors the server's auth-apply contract:
// every form is a flat key/value map under `fields`.
export default function AuthEditor({ kind, fields, onChange }: Props) {
  const set = (patch: Record<string, string>) => {
    onChange({ kind, fields: { ...fields, ...patch } });
  };
  const setKind = (next: AuthKind) => {
    // Switching to a new auth kind discards stale fields so a half-typed
    // OAuth config doesn't haunt a later Bearer save. We DO keep `none`
    // as the empty state and clear everything else when leaving it.
    onChange({ kind: next, fields: {} });
  };
  return (
    <div className="space-y-5 max-w-3xl">
      <div className="space-y-2">
        <label className="label">Authentication kind</label>
        <select
          className="input mono w-72"
          value={kind}
          onChange={(e) => setKind(e.target.value as AuthKind)}
        >
          <option value="none">None — send no credentials</option>
          <option value="basic">Basic — username + password</option>
          <option value="bearer">Bearer — opaque token</option>
          <option value="oauth2">OAuth 2.0 — token exchange + refresh</option>
          <option value="aws-sigv4">AWS Signature v4</option>
          <option value="digest">HTTP Digest (challenge/response)</option>
        </select>
        <Description kind={kind} />
      </div>

      {kind === "basic" && (
        <Section title="Basic credentials">
          <Field label="Username" value={fields.username ?? ""} onChange={(v) => set({ username: v })} />
          <Field label="Password" value={fields.password ?? ""} onChange={(v) => set({ password: v })} type="password" />
        </Section>
      )}

      {kind === "bearer" && (
        <Section title="Bearer token">
          <Field label="Token" value={fields.token ?? ""} onChange={(v) => set({ token: v })} type="password" mono />
          <p className="text-xs muted">Sent as <code className="mono">Authorization: Bearer &lt;token&gt;</code>. Use env vars (<code className="mono">{"{{API_TOKEN}}"}</code>) if you don't want the literal value here.</p>
        </Section>
      )}

      {kind === "oauth2" && <OAuth2Fields fields={fields} set={set} />}

      {kind === "aws-sigv4" && (
        <Section title="AWS credentials">
          <Field label="Access Key ID" value={fields.access_key ?? ""} onChange={(v) => set({ access_key: v })} mono />
          <Field label="Secret Access Key" value={fields.secret_key ?? ""} onChange={(v) => set({ secret_key: v })} type="password" mono />
          <Field label="Session Token (optional)" value={fields.session_token ?? ""} onChange={(v) => set({ session_token: v })} type="password" mono />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Region" value={fields.region ?? ""} onChange={(v) => set({ region: v })} placeholder="us-east-1" mono />
            <Field label="Service" value={fields.service ?? ""} onChange={(v) => set({ service: v })} placeholder="s3 / execute-api / ec2 / …" mono />
          </div>
          <p className="text-xs muted">Signs every outbound request with SigV4. <code className="mono">x-amz-content-sha256</code> is only added for S3 — other services follow the AWS SDK convention.</p>
        </Section>
      )}

      {kind === "digest" && (
        <Section title="HTTP Digest credentials">
          <Field label="Username" value={fields.username ?? ""} onChange={(v) => set({ username: v })} />
          <Field label="Password" value={fields.password ?? ""} onChange={(v) => set({ password: v })} type="password" />
          <div className="space-y-1">
            <label className="label">Algorithm</label>
            <select
              className="input mono w-48"
              value={fields.algorithm ?? "MD5"}
              onChange={(e) => set({ algorithm: e.target.value })}
            >
              <option value="MD5">MD5 (legacy)</option>
              <option value="SHA-256">SHA-256</option>
            </select>
          </div>
          <p className="text-xs muted">First fetch goes out unauthenticated; on the server's 401 challenge we compute the digest reply and re-send automatically.</p>
        </Section>
      )}
    </div>
  );
}

function OAuth2Fields({ fields, set }: { fields: Record<string, string>; set: (p: Record<string, string>) => void }) {
  const grant = fields.grant ?? "client_credentials";
  return (
    <Section title="OAuth 2.0 configuration">
      <div className="space-y-1">
        <label className="label">Grant type</label>
        <select
          className="input mono w-72"
          value={grant}
          onChange={(e) => set({ grant: e.target.value })}
        >
          <option value="client_credentials">client_credentials — machine-to-machine</option>
          <option value="authorization_code">authorization_code (with PKCE) — user sign-in</option>
          <option value="password">password — username + password</option>
        </select>
      </div>

      <Field label="Token URL" value={fields.token_url ?? ""} onChange={(v) => set({ token_url: v })} placeholder="https://idp.example.com/oauth/token" mono />
      {grant === "authorization_code" && (
        <Field label="Authorization URL" value={fields.auth_url ?? ""} onChange={(v) => set({ auth_url: v })} placeholder="https://idp.example.com/oauth/authorize" mono />
      )}

      <div className="grid grid-cols-2 gap-3">
        <Field label="Client ID" value={fields.client_id ?? ""} onChange={(v) => set({ client_id: v })} mono />
        <Field
          label={grant === "authorization_code" ? "Client Secret (optional with PKCE)" : "Client Secret"}
          value={fields.client_secret ?? ""}
          onChange={(v) => set({ client_secret: v })}
          type="password"
          mono
        />
      </div>

      {grant === "password" && (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Username" value={fields.username ?? ""} onChange={(v) => set({ username: v })} />
          <Field label="Password" value={fields.password ?? ""} onChange={(v) => set({ password: v })} type="password" />
        </div>
      )}

      {grant === "authorization_code" && (
        <Field label="Redirect URI" value={fields.redirect_uri ?? ""} onChange={(v) => set({ redirect_uri: v })} placeholder="https://apistash.dev/oauth/callback" mono />
      )}

      <div className="grid grid-cols-2 gap-3">
        <Field label="Scope (space-separated)" value={fields.scope ?? ""} onChange={(v) => set({ scope: v })} placeholder="read write" mono />
        <Field label="Audience (Auth0 / OIDC)" value={fields.audience ?? ""} onChange={(v) => set({ audience: v })} mono />
      </div>

      <p className="text-xs muted">
        We exchange these for an access token on first send and cache it (encrypted at rest) keyed by user. Tokens auto-refresh from <code className="mono">refresh_token</code> when present; otherwise we re-run the grant. Changing any field above invalidates the cache.
      </p>
    </Section>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-border p-4 space-y-3">
      <h3 className="text-sm font-semibold">{title}</h3>
      {children}
    </section>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, mono = false }: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  type?: "text" | "password";
  placeholder?: string;
  mono?: boolean;
}) {
  return (
    <div className="space-y-1">
      <label className="label">{label}</label>
      <input
        className={"input w-full " + (mono ? "mono" : "")}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        type={type}
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={false}
      />
    </div>
  );
}

function Description({ kind }: { kind: AuthKind }) {
  const msg = (() => {
    switch (kind) {
      case "none":      return "Send the request as-is.";
      case "basic":     return "Sends Authorization: Basic <base64(username:password)>.";
      case "bearer":    return "Sends Authorization: Bearer <token>.";
      case "oauth2":    return "Token exchange against an IdP, cached + refreshed per user. Supports machine-to-machine, user sign-in, and password grants.";
      case "aws-sigv4": return "Signs the request with AWS Signature v4 — works for any AWS API that accepts SigV4.";
      case "digest":    return "Two-leg HTTP Digest challenge/response (RFC 2617). First request goes out unauthenticated; the server's 401 nonce is used to compute the reply.";
      default:          return "";
    }
  })();
  if (!msg) return null;
  return <p className="text-xs muted">{msg}</p>;
}
